import { message } from 'antd'
import {
  AccessorialContext,
  type AccessorialFormType,
  type AccessorialOptionType,
  type FormActionType,
  FormActionTypes,
} from '../types'
import type { BookType, InvoiceDetail, InvoiceTransaction, TransactionRow } from '@/api/useFreight/types'
import type { TransactionDetail } from '../../columnDefs'
import { constructManualAccessorialPayload, getAccessorialOptions } from '../utils'
import { useTransactions } from '@/api/useFreight/useTransactions'
import { useAddFreightLineItemForm } from './useAddFreightLineItemForm'
import { useMemo } from 'react'
import { useRateManagement } from '@/api/useFreight/useFreight'
import { GridApi } from 'ag-grid-community'

export function useAddAccessorialForm(
  bookType: BookType,
  actionType: FormActionType,
  order: TransactionRow | InvoiceTransaction | undefined,
  fliDetail: InvoiceDetail | TransactionDetail | undefined,
  gridApi: GridApi,
  handleClose: () => void,
  context?: AccessorialContext,
  isCreditRebillMode?: boolean
) {
  const { form, isOverrideMutationPending, handleFLIOverride, handleCreditRebillFlow, resetForm } =
    useAddFreightLineItemForm<AccessorialFormType>(bookType, actionType, order, fliDetail, context)
  const { useAddManualAccessorialMutation } = useTransactions()
  const { useFilteredAccessorialsQuery } = useRateManagement()

  const { data: accessorials = [], isLoading: isAccessorialsLoading } = useFilteredAccessorialsQuery({
    order_id: order?.order_id || fliDetail?.order_id || '',
    book_type: bookType,
    customer_id: order?.customer_id || fliDetail?.customer_id || undefined,
  })
  const accessorialOptions: AccessorialOptionType[] = useMemo(() => getAccessorialOptions(accessorials), [accessorials])

  const initialValues = useMemo(() => {
    if (!fliDetail) return
    return {
      rate: fliDetail.rate,
      amount: fliDetail.amount,
      total: fliDetail.total,
      origin: fliDetail.origin_name ? { label: fliDetail.origin_name, value: fliDetail.origin_id } : undefined,
      destination: fliDetail.destination_name
        ? { label: fliDetail.destination_name, value: fliDetail.destination_id }
        : undefined,
    }
  }, [fliDetail])

  async function handleSubmit() {
    const values = form.getFieldsValue()
    let selectedAccessorial: AccessorialOptionType | TransactionDetail | InvoiceDetail | undefined = fliDetail
    if (actionType === FormActionTypes.create) {
      selectedAccessorial = accessorialOptions.find((option) => option.value === values.accessorial_id)
    }
    try {
      if (isCreditRebillMode) {
        await handleCreditRebillFlow(values, 'accessorial', selectedAccessorial)
      } else if (actionType === FormActionTypes.create) {
        const payload = constructManualAccessorialPayload(values, bookType)
        await useAddManualAccessorialMutation.mutateAsync({
          ...payload,
          order_id: order?.order_id || '',
          customer_id: order?.customer_id || undefined,
        })
        message.success(`Successfully added ${selectedAccessorial?.label}.`)
      } else if (actionType === FormActionTypes.edit) {
        await handleFLIOverride(values)
      }
      gridApi?.refreshCells({ force: true })
      gridApi?.resetRowHeights()
    } catch (error) {
      message.error('An error occurred while processing the request. Please try again.')
    } finally {
      handleClose()
      resetForm()
    }
  }

  return {
    form,
    bookType,
    accessorialOptions,
    isAccessorialsLoading,
    isAccessorialMutationPending: useAddManualAccessorialMutation.isPending || isOverrideMutationPending,
    handleSubmit,
    initialValues,
    resetForm,
  }
}
